/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { ButtonProps } from "@insite/mobius/Button/Button";
import buttonSizeVariants from "@insite/mobius/Button/buttonSizeVariants";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xBaseTheme } from "@monsma/XDL/types";
import { css } from "styled-components";

type BWT = Pick<ButtonProps, "shadow"> &
    InjectableCss & {
        _sizeVariant: ButtonProps["sizeVariant"];
        _size: ButtonProps["size"] | null;
        _color: ButtonProps["color"];
        _shape: ButtonProps["shape"];
    };

const mainColor = "common.ui2";
const hoverColor = "common.ui3";
const activeColor = "common.ui4";
const backgroundColor = "common.background";

const buttonPresentationStyle = (buttonVariant: string) => {
    return {
        ...ButtonPresentationPropsDefault,
        buttonType: "solid",
        sizeVariant: "large",
        mergeCss: true,
        typographyProps: {
            size: "14px",
            forwardAs: "span",
            weight: "bold",
            css: css`
                letter-spacing: 0.25px;
                line-height: inherit;
                max-width: 100%;
                overflow: hidden;
                display: inline-block;
                text-overflow: ellipsis;
                margin-top: 0;
                margin-bottom: 0;
            `,
        },
        css: css`
            display: inline-flex;
            min-height: 0;
            height: ${(props: BWT) => props["_size"] || get(buttonSizeVariants, [props["_sizeVariant"], "height"])}px;
            backface-visibility: visible;
            border-radius: 4px;
            border-width: 1px;

            &:focus {
                box-shadow: 1px 1px 1px transparent;
            }

            && {
                line-height: 22px;
                white-space: nowrap;
                text-align: center;
                align-items: center;
                justify-content: center;
                width: auto;

                ${
                    buttonVariant === "secondary" &&
                    css`
                        border-color: ${getColor(mainColor)};
                        color: ${getColor(mainColor)};
                    `
                }

                ${
                    buttonVariant === "tertiary" &&
                    css`
                        border-color: ${getColor(backgroundColor)};
                        color: ${getColor(backgroundColor)};
                        background: transparent;
                    `
                }
            }

            &&:hover {
                border-color: ${getColor(hoverColor)};
                ${
                    buttonVariant === "primary"
                        ? css`
                              background: ${getColor(hoverColor)};
                          `
                        : css`
                              background: ${getColor(backgroundColor)};
                              color: ${getColor(hoverColor)};
                          `
                }
            }

            &&:active,
            &&:focus {
                border-color: ${getColor(activeColor)};

                ${
                    buttonVariant === "primary"
                        ? css`
                              background: ${getColor(activeColor)};
                          `
                        : css`
                              background: ${getColor(backgroundColor)};
                              color: ${getColor(activeColor)};
                          `
                }
            }

            &&:disabled {
                opacity: 0.33;

                ${
                    buttonVariant === "primary" &&
                    css`
                        border-color: ${getColor(mainColor)};
                        background: ${getColor(mainColor)};
                    `
                }
                
                ${
                    buttonVariant === "secondary" &&
                    css`
                        border-color: ${getColor(mainColor)};
                        background: ${getColor(backgroundColor)};
                        color: ${getColor(mainColor)};
                    `
                }
                
                ${
                    buttonVariant === "tertiary" &&
                    css`
                        border-color: ${getColor(backgroundColor)};
                        color: ${getColor(backgroundColor)};
                        background: transparent;
                    `
                }
            }
        `,
    };
};

buttonSizeVariants.small = {
    height: 36,
    padding: "0 15px",
    fontSize: 12,
    icon: 11,
    iconPadding: 5,
};

buttonSizeVariants.medium = {
    height: 42,
    padding: "0 20px",
    fontSize: 12,
    icon: 11,
    iconPadding: 5,
};

buttonSizeVariants.large = {
    height: 46,
    padding: "0 30px",
    fontSize: 13,
    icon: 12,
    iconPadding: 5,
};

export default {
    primary: {
        ...buttonPresentationStyle("primary"),
        color: mainColor,
    },
    secondary: {
        ...buttonPresentationStyle("secondary"),
        color: backgroundColor,
    },
    tertiary: {
        ...buttonPresentationStyle("tertiary"),
        color: backgroundColor,
    },
} as xBaseTheme["button"];
